import React from 'react';
import { Form } from 'react-bootstrap';
import { fieldMessage, hasError } from './blockUtils';

export const WORK_STUDY_ID = 'sa-work-study';
export const HOLDS_POSITION_ID = 'sa-holds-campus-position';
export const CAMPUS_HOURS_ID = 'sa-campus-hours';

const MAX_HOURS = 40;

const WORK_STUDY_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'not_sure', label: 'Not sure' },
];

function hoursAreValid(hours) {
    if (hours === undefined || hours === null || hours === '') return true;
    const n = Number(hours);
    return Number.isFinite(n) && n >= 0 && n <= MAX_HOURS;
}

// Mirrors the server's submit rules: the first question always, the second
// after a Yes, and an hours value (optional) only within 0 to 40.
export function getErrors(answers) {
    const ce = answers?.campusEmployment || {};
    const errors = [];
    if (!['yes', 'no', 'not_sure'].includes(ce.workStudy)) {
        errors.push({
            id: WORK_STUDY_ID,
            label: 'Work-study award question',
        });
    } else if (
        ce.workStudy === 'yes' &&
        typeof ce.holdsCampusPosition !== 'boolean'
    ) {
        errors.push({
            id: HOLDS_POSITION_ID,
            label: 'Campus position question',
        });
    }
    if (
        ce.workStudy === 'yes' &&
        ce.holdsCampusPosition === true &&
        !hoursAreValid(ce.hoursPerWeek)
    ) {
        errors.push({
            id: CAMPUS_HOURS_ID,
            label: 'Campus position hours per week',
        });
    }
    return errors;
}

export function isComplete(answers) {
    return getErrors(answers).length === 0;
}

function RadioGroup({
    id,
    legend,
    options,
    value,
    onSelect,
    invalid,
    message,
}) {
    const errorId = `${id}-error`;
    return (
        <fieldset
            role="radiogroup"
            className="mb-3"
            aria-required="true"
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
        >
            <legend className="fs-6 fw-semibold mb-2">
                {legend}{' '}
                <span className="text-danger" aria-hidden="true">
                    *
                </span>
            </legend>
            {options.map((option, index) => (
                <Form.Check
                    key={String(option.value)}
                    type="radio"
                    id={index === 0 ? id : `${id}-${String(option.value)}`}
                    name={id}
                    label={option.label}
                    checked={value === option.value}
                    onChange={() => onSelect(option.value)}
                />
            ))}
            {invalid && (
                <Form.Control.Feedback
                    type="invalid"
                    id={errorId}
                    role="alert"
                    className="d-block"
                >
                    {message || 'Answer this question.'}
                </Form.Control.Feedback>
            )}
        </fieldset>
    );
}

// context.weeklyHoursLimit is the combined campus-job limit from the
// student-employment settings; without it the help text is left out.
function CampusEmployment({ answers, onChange, errorIds, context }) {
    const ce = answers.campusEmployment || {};
    const limit = context?.weeklyHoursLimit;
    const termName = context?.termName;
    const internationalLimit = context?.internationalWeeklyHoursLimit;
    const helpId = `${CAMPUS_HOURS_ID}-help`;
    const hoursInvalid = hasError(errorIds, CAMPUS_HOURS_ID);
    const hoursErrorId = `${CAMPUS_HOURS_ID}-error`;

    const setWorkStudy = (workStudy) => {
        if (workStudy === 'yes') {
            onChange('campusEmployment', { ...ce, workStudy });
        } else {
            onChange('campusEmployment', { workStudy });
        }
    };

    const setHolds = (holdsCampusPosition) => {
        if (holdsCampusPosition) {
            onChange('campusEmployment', { ...ce, holdsCampusPosition });
        } else {
            onChange('campusEmployment', {
                workStudy: ce.workStudy,
                holdsCampusPosition,
            });
        }
    };

    return (
        <div>
            <h3 className="h5 mb-3">Other campus employment</h3>
            <RadioGroup
                id={WORK_STUDY_ID}
                legend={`Have you been awarded work-study funds (State of Colorado or Federal)${termName ? ` for ${termName}` : ''}?`}
                options={WORK_STUDY_OPTIONS}
                value={ce.workStudy}
                onSelect={setWorkStudy}
                invalid={
                    hasError(errorIds, WORK_STUDY_ID) ||
                    Boolean(fieldMessage(context, WORK_STUDY_ID))
                }
                message={fieldMessage(context, WORK_STUDY_ID)}
            />

            <div aria-live="polite">
                {ce.workStudy === 'yes' && (
                    <RadioGroup
                        id={HOLDS_POSITION_ID}
                        legend="Do you currently hold a position on campus?"
                        options={[
                            { value: true, label: 'Yes' },
                            { value: false, label: 'No' },
                        ]}
                        value={ce.holdsCampusPosition}
                        onSelect={setHolds}
                        invalid={
                            hasError(errorIds, HOLDS_POSITION_ID) ||
                            Boolean(fieldMessage(context, HOLDS_POSITION_ID))
                        }
                        message={fieldMessage(context, HOLDS_POSITION_ID)}
                    />
                )}
                {ce.workStudy === 'yes' && ce.holdsCampusPosition === true && (
                    <Form.Group className="mb-3" controlId={CAMPUS_HOURS_ID}>
                        <Form.Label>
                            About how many hours per week do those position(s)
                            total?
                        </Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            max={MAX_HOURS}
                            step="any"
                            className="w-auto"
                            value={ce.hoursPerWeek ?? ''}
                            onChange={(e) =>
                                onChange('campusEmployment', {
                                    ...ce,
                                    hoursPerWeek: e.target.value,
                                })
                            }
                            isInvalid={hoursInvalid}
                            aria-invalid={hoursInvalid}
                            aria-describedby={
                                [
                                    typeof limit === 'number' ? helpId : null,
                                    hoursInvalid ? hoursErrorId : null,
                                ]
                                    .filter(Boolean)
                                    .join(' ') || undefined
                            }
                        />
                        {typeof limit === 'number' && (
                            <Form.Text id={helpId} muted>
                                Campus jobs are limited to {limit} hours a week
                                combined
                                {typeof internationalLimit === 'number' &&
                                    ` (${internationalLimit} for international students)`}
                                .
                            </Form.Text>
                        )}
                        {hoursInvalid && (
                            <Form.Control.Feedback
                                type="invalid"
                                id={hoursErrorId}
                                role="alert"
                                className="d-block"
                            >
                                Enter a number from 0 to {MAX_HOURS}.
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                )}
            </div>
        </div>
    );
}

export default CampusEmployment;
